/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {getGridColumnState, onSetGridColumns, onUpdateGridColumns} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {AdminAction, AsyncState} from '@ion/applications/src/types/redux';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  PegBehavior,
  PegBehaviors,
  PegBehaviorsFilters,
  PegLabels
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  hasEventAccumLabelsExists,
  uniquePegLabels
} from '@/routes/admin/pegbehaviors/PegEventAccumulatorUtils';
import {
  ADD_ADMIN_PEG_BEHAVIOR_ERROR,
  ADD_ADMIN_PEG_BEHAVIOR_REQUEST,
  ADD_ADMIN_PEG_BEHAVIOR_RESPONSE,
  ADMIN_PEG_BEHAVIORS_GRID_REFRESHED,
  AUDIT_PEG_BEHAVIOR,
  AUDIT_PEG_BEHAVIOR_SHOW_CHANGES_DIALOG,
  CLONE_ADMIN_PEG_BEHAVIOR_ERROR,
  CLONE_ADMIN_PEG_BEHAVIOR_REQUEST,
  CLONE_ADMIN_PEG_BEHAVIOR_RESPONSE,
  CLONE_PEG_BEHAVIOR,
  DELETE_ADMIN_PEG_BEHAVIOR_ERROR,
  DELETE_ADMIN_PEG_BEHAVIOR_REQUEST,
  DELETE_ADMIN_PEG_BEHAVIOR_RESPONSE,
  DELETE_PEG_BEHAVIOR,
  EDIT_ADMIN_PEG_BEHAVIOR_ERROR,
  EDIT_ADMIN_PEG_BEHAVIOR_REQUEST,
  EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE,
  GET_ADMIN_PB_PEG_LABELS_ERROR,
  GET_ADMIN_PB_PEG_LABELS_REQUEST,
  GET_ADMIN_PB_PEG_LABELS_RESPONSE,
  GET_ADMIN_PEG_BEHAVIORS_ERROR,
  GET_ADMIN_PEG_BEHAVIORS_REQUEST,
  GET_ADMIN_PEG_BEHAVIORS_RESPONSE,
  INITIALIZE_ADMIN_PEG_BEHAVIORS,
  RESET_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES,
  SET_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS,
  SET_EXCLUDE_INHERITED_PEGBEHAVIORS,
  SET_GRID_PAGE_SIZE,
  SHOW_ADD_PEG_BEHAVIOR_DIALOG,
  SHOW_EDIT_PEG_BEHAVIOR_DIALOG,
  SHOW_VIEW_PEG_BEHAVIOR_DIALOG,
  UPDATE_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS,
  UPDATE_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES,
  VIEW_ADMIN_PEG_BEHAVIOR_ERROR,
  VIEW_ADMIN_PEG_BEHAVIOR_REQUEST,
  VIEW_ADMIN_PEG_BEHAVIOR_RESPONSE
} from '../actions/admin-pegbehaviors';

//defines the user preference key for the admin applications page ...
export const PREFERENCE = 'admin-pegbehaviors';

export interface PegBehaviorsAuditState extends PegBehaviorsActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

interface PegBehaviorsActionState extends AsyncState {
  selected: PegBehavior | PegBehaviors | null;
  result: PegBehavior | PegBehaviors | null;
  showDialog: boolean;
  showStatusDialog?: boolean;
  isView?: boolean;
};

export interface AdminPegBehaviorsAction extends AdminAction {
  pegBehaviors?: PegBehaviors;
  pegBehavior?: PegBehavior;
  searchParams?: PegBehaviorsFilters;
  excludeInheritedPegBehaviors?: boolean;
  showDialog: boolean;
  showStatusDialog?: boolean;
  isView?: boolean;
};

export interface AdminPegBehaviorsDestroyState extends AsyncState {
  selected: PegBehavior | PegBehaviors | null;
  result: PegBehavior | PegBehaviors | null;
  showDialog: boolean;
};

export interface AdminPegBehaviorsCloneState extends AsyncState {
  selected: PegBehavior | PegBehaviors | null;
  result: PegBehavior | PegBehaviors | null;
  showDialog: boolean;
};

export interface AdminPegBehaviorsState extends PageState {
  pegBehaviors: {
    error: ServiceError;
    loading: boolean;
  };
  pegLabels: {
    loading: boolean;
    error: ServiceError | null;
    result: PegLabels;
  },
  grid: {
    columnState: ColumnState[];
    preferedColumnState: ColumnState[];
    hasUpdate: boolean;
    pageSize: number;
    excludeInheritedPegBehaviors: boolean;
  };
  add: PegBehaviorsActionState;
  edit: PegBehaviorsActionState;
  view: PegBehaviorsActionState;
  audit: PegBehaviorsAuditState;
  destroy: AdminPegBehaviorsDestroyState;
  clone: AdminPegBehaviorsCloneState;
  search: PegBehaviorsFilters;
};

//defines the initial state for the reducer ...
export const initialState: AdminPegBehaviorsState = {
  isInitialized : false,
  pegBehaviors  : {
    error   : null,
    loading : false
  },
  pegLabels : {
    loading : false,
    error   : null,
    result  : []
  },
  grid : {
    columnState                  : null,
    preferedColumnState          : null,
    hasUpdate                    : false,
    pageSize                     : gridDefaultPageSize,
    excludeInheritedPegBehaviors : false
  },
  add : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  edit : {
    showDialog       : false,
    showStatusDialog : false,
    selected         : null,
    loading          : false,
    error            : null,
    result           : null,
    isView           : false
  },
  view : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isView     : false
  },
  audit : {
    showDialog        : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  destroy : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  clone : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  search : {
    name   : null,
    status : null
  }
};

//defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_PEG_BEHAVIORS] : (draft: AdminPegBehaviorsState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.search.name              = null;
    draft.search.status            = null;
  },
  [ADMIN_PEG_BEHAVIORS_GRID_REFRESHED] : ({add, edit, clone, destroy}: AdminPegBehaviorsState) => {
    destroy.result = null;
    destroy.error  = null;
    add.result = null;
    edit.result = null;
    clone.result = null;
  },
  [SET_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS] : ({grid, pegBehaviors}: AdminPegBehaviorsState, {columnState}) => {
    onSetGridColumns({
      isLoading  : pegBehaviors.loading,
      preference : PREFERENCE,
      grid
    }, columnState);
  },
  [UPDATE_ADMIN_PEG_BEHAVIORS_GRID_COLUMNS] : ({grid}: AdminPegBehaviorsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_ADMIN_PEG_BEHAVIORS_REQUEST] : ({pegBehaviors}: AdminPegBehaviorsState) => {
    pegBehaviors.loading = true;
  },
  [GET_ADMIN_PEG_BEHAVIORS_RESPONSE] : ({pegBehaviors}: AdminPegBehaviorsState) => {
    pegBehaviors.loading = false;
  },
  [GET_ADMIN_PEG_BEHAVIORS_ERROR] : ({pegBehaviors}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    pegBehaviors.loading = false;
    pegBehaviors.error   = error;
  },
  [SHOW_ADD_PEG_BEHAVIOR_DIALOG] : ({add}: AdminPegBehaviorsState, {showDialog}: AdminPegBehaviorsAction) => {
    add.showDialog = showDialog;
  },
  [DELETE_PEG_BEHAVIOR] : ({destroy}: AdminPegBehaviorsState, {pegBehavior, show}: AdminPegBehaviorsAction) => {
    destroy.selected = show ? pegBehavior : null;
    destroy.showDialog = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_PEG_BEHAVIOR_REQUEST] : ({destroy}: AdminPegBehaviorsState) => {
    destroy.loading = true;
    destroy.error = null;
    destroy.result = null;
  },
  [DELETE_ADMIN_PEG_BEHAVIOR_RESPONSE] : ({destroy}: AdminPegBehaviorsState,
    {pegBehavior}: AdminPegBehaviorsAction) => {
    destroy.result = pegBehavior;
    destroy.selected = null;
    destroy.loading = destroy.showDialog = false;
  },
  [DELETE_ADMIN_PEG_BEHAVIOR_ERROR] : ({destroy}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    destroy.error = error;
    destroy.result = null;
    destroy.loading = destroy.showDialog = false;
  },
  [CLONE_PEG_BEHAVIOR] : ({clone}: AdminPegBehaviorsState, {pegBehavior, show}: AdminPegBehaviorsAction) => {
    clone.selected = show ? pegBehavior : null;
    clone.showDialog = show;
    clone.result = null;
  },
  [CLONE_ADMIN_PEG_BEHAVIOR_REQUEST] : ({clone}: AdminPegBehaviorsState) => {
    clone.loading = true;
    clone.error = null;
    clone.result = null;
  },
  [CLONE_ADMIN_PEG_BEHAVIOR_RESPONSE] : ({clone}: AdminPegBehaviorsState,
    {pegBehavior}: AdminPegBehaviorsAction) => {
    clone.result = pegBehavior;
    clone.selected = null;
    clone.loading = clone.showDialog = false;
  },
  [CLONE_ADMIN_PEG_BEHAVIOR_ERROR] : ({clone}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    clone.error = error;
    clone.result = null;
    clone.loading = clone.showDialog = false;
  },
  [UPDATE_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES] : ({search}: AdminPegBehaviorsState, {searchParams}) => {
    search.name = searchParams.name ?? null;
    search.status = searchParams.status ?? null;
  },
  [RESET_ADMIN_PEG_BEHAVIORS_SEARCH_QUERIES] : ({search}: AdminPegBehaviorsState) => {
    search.name = null;
    search.status = null;
  },

  [ADD_ADMIN_PEG_BEHAVIOR_REQUEST] : ({add}: AdminPegBehaviorsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_PEG_BEHAVIOR_RESPONSE] : ({add}: AdminPegBehaviorsState, {pegBehavior}: AdminPegBehaviorsAction) => {
    add.result = pegBehavior;
    add.selected = null;
    add.loading = add.showDialog = false;
  },
  [ADD_ADMIN_PEG_BEHAVIOR_ERROR] : ({add}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    add.error = error;
    add.result = null;
    add.loading = add.showDialog = false;
  },
  [SHOW_EDIT_PEG_BEHAVIOR_DIALOG] : (
    {edit}: AdminPegBehaviorsState,
    {showDialog, pegBehavior, isView, showStatusDialog}: AdminPegBehaviorsAction
  ) => {
    edit.selected         = showDialog ? pegBehavior : null;
    edit.result           = null;
    edit.showDialog       = showDialog;
    edit.isView           = isView;
    edit.showStatusDialog = showStatusDialog;
  },
  [EDIT_ADMIN_PEG_BEHAVIOR_REQUEST] : ({edit}: AdminPegBehaviorsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_PEG_BEHAVIOR_RESPONSE] : ({edit}: AdminPegBehaviorsState, {pegBehavior}: AdminPegBehaviorsAction) => {
    edit.result = pegBehavior;
    edit.selected = null;
    edit.loading = edit.showDialog = edit.showStatusDialog = false;
  },
  [EDIT_ADMIN_PEG_BEHAVIOR_ERROR] : ({edit}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    edit.error = error;
    edit.result = edit.selected = null;
    edit.loading = edit.showDialog = edit.showStatusDialog = false;
  },
  [SHOW_VIEW_PEG_BEHAVIOR_DIALOG] : (
    {view}: AdminPegBehaviorsState,
    {showDialog, pegBehavior}: AdminPegBehaviorsAction
  ) => {
    view.selected      = showDialog ? pegBehavior : null;
    view.result        = null;
    view.showDialog    = showDialog;
  },
  [VIEW_ADMIN_PEG_BEHAVIOR_REQUEST] : ({view}: AdminPegBehaviorsState) => {
    view.loading = true;
    view.error = view.result = null;
  },
  [VIEW_ADMIN_PEG_BEHAVIOR_RESPONSE] : ({view}: AdminPegBehaviorsState, {pegBehavior}: AdminPegBehaviorsAction) => {
    view.result = pegBehavior;
    view.selected = null;
    view.loading = view.showDialog = false;
  },
  [VIEW_ADMIN_PEG_BEHAVIOR_ERROR] : ({view}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    view.error = error;
    view.result = view.selected = null;
    view.loading = view.showDialog = false;
  },
  [AUDIT_PEG_BEHAVIOR] : ({audit}: AdminPegBehaviorsState, {pegBehavior, show}: AdminPegBehaviorsAction) => {
    audit.selected   = show ? pegBehavior : null;
    audit.showDialog = show;
  },
  [AUDIT_PEG_BEHAVIOR_SHOW_CHANGES_DIALOG] : ({audit}: AdminPegBehaviorsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminPegBehaviorsState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [SET_EXCLUDE_INHERITED_PEGBEHAVIORS] : ({grid}: AdminPegBehaviorsState, {excludeInheritedPegBehaviors}) => {
    grid.excludeInheritedPegBehaviors = excludeInheritedPegBehaviors;
  },

  [GET_ADMIN_PB_PEG_LABELS_REQUEST] : ({pegLabels}: AdminPegBehaviorsState, isSearch) => {
    pegLabels.loading = true;
    pegLabels.error   = null;
    pegLabels.result  = isSearch?.isSearch ? [...pegLabels.result] : [];
  },
  [GET_ADMIN_PB_PEG_LABELS_RESPONSE] : ({pegLabels}: AdminPegBehaviorsState, {allPegLabels}) => {
    pegLabels.result              = uniquePegLabels([...pegLabels.result, ...allPegLabels]);
    const isEventAccumLabelsExist = hasEventAccumLabelsExists(pegLabels.result);
    pegLabels.loading             = !isEventAccumLabelsExist;
    pegLabels.error               = null;
  },
  [GET_ADMIN_PB_PEG_LABELS_ERROR] : ({pegLabels}: AdminPegBehaviorsState, {error}: AdminPegBehaviorsAction) => {
    pegLabels.loading = false;
    pegLabels.error   = error;
    pegLabels.result  = [];
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminPegBehaviorsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
